// The citation chips attached to an agent turn (F-AGENT PR-B).
//
// W7 — chips render ONLY from the BE-built `AgentCitationChip` descriptors
// (the `registry`/`citations` SSE payload, attached to the message by
// `useAgent`). A bare `[REF:x]` in the streamed text with no registry entry
// is NEVER turned into a chip here — it stays plain text in the bubble. The
// add-in never invents a citation's existence or its verified state.
//
// §9 — logical RTL utilities only; chip labels get per-string direction via
// `bidiProps` (a chip can be Arabic in an FR chrome and vice-versa).

import { bidiProps } from '@/lib/bidi'
import type { AgentCitationChip } from '@/domain/agent/entity'

interface CitationChipsProps {
  chips: AgentCitationChip[]
}

export function CitationChips({ chips }: CitationChipsProps): JSX.Element | null {
  if (chips.length === 0) return null
  return (
    <div
      data-testid="agent-citation-chips"
      className="mt-2 flex flex-col gap-1"
    >
      {chips.map((chip) => (
        <CitationChip key={chip.refKey} chip={chip} />
      ))}
    </div>
  )
}

interface CitationChipProps {
  chip: AgentCitationChip
}

/** A single read-only chip. Presentation only — the label is built from the
 *  BE-supplied codeName/articleNumber/refKey, never computed locally. */
function CitationChip({ chip }: CitationChipProps): JSX.Element {
  const label = chipLabel(chip)
  return (
    <span
      data-testid={`agent-citation-chip-${chip.refKey}`}
      className="inline-flex items-start gap-1.5 rounded bg-gold/10 px-2 py-1 text-[11px] leading-snug text-ink/80"
    >
      <span
        aria-hidden="true"
        className="mt-px inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-gold"
      />
      <span className="font-medium" {...bidiProps(label)}>
        {label}
      </span>
    </span>
  )
}

/** Compose the chip's display label from the BE descriptor. Falls back to the
 *  canonical refKey when the human-readable parts are absent — never blank,
 *  never fabricated. */
function chipLabel(chip: AgentCitationChip): string {
  const parts = [chip.codeName, chip.articleNumber].filter(
    (p): p is string => typeof p === 'string' && p.length > 0,
  )
  return parts.length > 0 ? parts.join(' ') : chip.refKey
}
